import { useState } from "react";
import {
  HiOutlineCalendar,
  HiOutlineExclamationTriangle,
} from "react-icons/hi2";
import { parseUsDate } from "../../../utils/utils";
import { cn } from "../../../lib/cn";
import Pagination from "../../shared/Pagination";

const PAGE_SIZE = 4;

const PRIORITY_STYLES = {
  HIGH: { bg: "bg-danger/10", text: "text-danger", border: "border-danger/30" },
  MEDIUM: {
    bg: "bg-secondary/10",
    text: "text-primary",
    border: "border-secondary/30",
  },
  LOW: {
    bg: "bg-success/10",
    text: "text-success",
    border: "border-success/30",
  },
};

const getDaysLeftLabel = (task) => {
  const due = parseUsDate(task.due_date);
  if (!due || Number.isNaN(due.getTime())) return null;

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);

  const diffMs = due - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Due today";
  if (diffDays === 1) return "1 day left";
  if (diffDays > 1) return `${diffDays} days left`;
  if (diffDays === -1) return "1 day overdue";
  return `${Math.abs(diffDays)} days overdue`;
};

const UpcomingDeadlines = ({ tasks = [], isLoading = false }) => {
  const [page, setPage] = useState(1);
  const now = new Date();
  const totalPages = Math.max(1, Math.ceil(tasks.length / PAGE_SIZE));
  const startIdx = (page - 1) * PAGE_SIZE;
  const paged = tasks.slice(startIdx, startIdx + PAGE_SIZE);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-surface-border">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-primary">
            Upcoming Deadlines
          </h2>
          <HiOutlineCalendar className="w-5 h-5 text-mediumgrey" />
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="p-3 rounded-lg bg-surface min-h-[44px] motion-safe:animate-pulse"
            >
              <div className="h-4 bg-surface-disabled rounded w-3/4 mb-1.5" />
              <div className="h-3 bg-surface-disabled rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-surface-border">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-primary">
          Upcoming Deadlines
        </h2>
        <HiOutlineCalendar className="w-5 h-5 text-mediumgrey" />
      </div>

      {tasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <div className="w-10 h-10 bg-surface-disabled rounded-full flex items-center justify-center mb-2">
            <HiOutlineCalendar className="w-5 h-5 text-grey" />
          </div>
          <p className="text-sm font-medium text-primary">
            No upcoming deadlines
          </p>
          <p className="text-xs text-grey mt-1">
            No tasks due in the next 30 days.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2 min-h-[368px]">
            {paged.map((task) => {
              const priority = task.priority || "MEDIUM";
              const styles = PRIORITY_STYLES[priority];
              const dueDate = parseUsDate(task.due_date);
              const isOverdue = dueDate ? dueDate < now : false;
              const daysLabel = getDaysLeftLabel(task);

              return (
                <div
                  key={task.id}
                  className="p-3 rounded-lg bg-surface hover:bg-surface-hover transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-darkgrey truncate">
                        {task.title}
                      </p>
                      {task.project_title && (
                        <p className="text-xs text-grey mt-0.5 truncate">
                          {task.project_title}
                        </p>
                      )}
                    </div>
                    <span
                      className={cn(
                        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold whitespace-nowrap min-h-[22px]",
                        styles.bg,
                        styles.text,
                        styles.border,
                      )}
                    >
                      {priority.toLowerCase()}
                    </span>
                  </div>
                  {isOverdue && daysLabel && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-danger bg-danger/10 px-1.5 py-1 rounded w-fit">
                      <HiOutlineExclamationTriangle className="w-3 h-3" />
                      <span>{daysLabel}</span>
                    </div>
                  )}
                  {!isOverdue && daysLabel && (
                    <div className="text-xs text-mediumgrey mt-2">
                      {daysLabel}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={tasks.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
};

export default UpcomingDeadlines;
